import { motion } from "framer-motion";
import WireframeIcon from "./WireframeIcon";

const services = [
  {
    id: "01",
    title: "Web Applications",
    desc: "High-performance, deeply interactive sites and dashboards. Next-gen stacks, motion, three-dimensional storytelling.",
    shape: "torus",
    tags: ["React", "Next", "Three.js", "WebGL"],
  },
  {
    id: "02",
    title: "Mobile Products",
    desc: "Native-feeling iOS / Android apps with a single codebase. Pixel-tight, gesture-driven, instantly responsive.",
    shape: "capsule",
    tags: ["React Native", "Expo", "Swift", "Kotlin"],
  },
  {
    id: "03",
    title: "AI Integration",
    desc: "LLM workflows, RAG, agents, vision, voice. We embed intelligence into the products you already ship.",
    shape: "icosa",
    tags: ["OpenAI", "Claude", "Gemini", "Embeddings"],
  },
  {
    id: "04",
    title: "Custom SaaS",
    desc: "Multi-tenant platforms with billing, roles, analytics. Built to scale from the first user to the millionth.",
    shape: "octa",
    tags: ["Postgres", "Stripe", "Edge", "Realtime"],
  },
  {
    id: "05",
    title: "CRM & Internal Tools",
    desc: "Operator-grade dashboards your team will actually use. Speed, keyboard-first, surgical workflows.",
    shape: "cube",
    tags: ["Pipelines", "Automations", "Roles", "Reports"],
  },
  {
    id: "06",
    title: "Any Custom Software",
    desc: "If it can be built, we'll architect, design and ship it. Long-term partnerships, not vendor contracts.",
    shape: "knot",
    tags: ["Architecture", "DevOps", "Audits", "Scale"],
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#050505] py-32 md:py-48"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-20 md:mb-28">
          <div className="md:col-span-4">
            <div className="font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/50">
              (02) — Capabilities
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display font-extrabold uppercase leading-[0.9] tracking-tighter text-4xl md:text-6xl lg:text-7xl">
              Six disciplines.<br />
              <span className="text-[#29ABE2]/80">One obsession:</span><br />
              shipping.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/[0.06]">
          {services.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.05 }}
              className="group relative border-r border-b border-white/[0.06] p-8 md:p-10 lg:p-12 hover:bg-white/[0.02] transition-colors duration-500"
              data-testid={`service-card-${s.id}`}
              data-cursor-hover
            >
              <div className="flex items-start justify-between mb-10 md:mb-14">
                <span className="font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/40">
                  {s.id}
                </span>
                <div className="-mt-2 -mr-2 transition-transform duration-500 group-hover:scale-110">
                  <WireframeIcon shape={s.shape} size={84} />
                </div>
              </div>

              <h3 className="font-display font-bold text-2xl md:text-3xl mb-4 tracking-tight">
                {s.title}
              </h3>
              <p className="text-white/60 text-sm md:text-base leading-relaxed mb-8">
                {s.desc}
              </p>

              <div className="flex flex-wrap gap-2">
                {s.tags.map((t) => (
                  <span
                    key={t}
                    className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/50 border border-white/[0.08] px-2.5 py-1 rounded-full"
                  >
                    {t}
                  </span>
                ))}
              </div>

              <div className="absolute bottom-0 left-0 h-px bg-[#29ABE2] scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-700 w-full" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
